import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useJobs } from "../../../hooks/useJobs";
import { SHADOW } from "../../../theme/tokens";
import type { TeamNurse } from "../../../utils/models";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { type SystemChipItem, SystemChips } from "../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import { CONFIG_TABLE_BODY_CELL_SX, CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import type { HolidayStaffFilter } from "../teamHolidays";
import { HolidayBalanceCell } from "./HolidayBalanceCell";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HolidayGroupChip } from "./HolidayGroupChip";
import { HolidaySectionError } from "./HolidaySectionError";
import type { HolidayGroup, HolidayPerson } from "./holidayApi";
import { BALANCE_ROWS, computeGroupBalance, DAY_PART_LABEL_KEY, HOLIDAY_NONE, holidayGroupColor, NO_DAY_PART_KEY } from "./holidayFormat";

/** The job filter's "every job" chip. Not a job name, so it can't clash with one. */
const ALL_JOBS = "";

type Props = {
	groups: HolidayGroup[];
	/** Active people; undefined while loading. */
	people: HolidayPerson[] | undefined;
	/** The people or the roster couldn't load. */
	isError: boolean;
	onRetry: () => void;
	/** The department's roster, for names and jobs; undefined while loading. */
	roster: TeamNurse[] | undefined;
	onShowOnTeam: (filter: HolidayStaffFilter) => void;
};

/** Active staff per group and day part, so the scheduler can keep each day part covered on every holiday. */
export const HolidayGroupBalanceSection: FC<Props> = ({ groups, people, isError, onRetry, roster, onShowOnTeam }) => {
	const { t } = useTranslation();
	const jobs = useJobs();
	const { track } = useAnalytics();
	const [jobName, setJobName] = useState<string>(ALL_JOBS);
	const jobItems = useMemo<SystemChipItem<string>[]>(
		() => [{ value: ALL_JOBS, label: t("holidays.balance.allJobs") }, ...jobs.map((job) => ({ value: job.name, label: job.displayName }))],
		[jobs, t],
	);
	const jobNamesByNurseId = useMemo(
		() =>
			roster === undefined
				? undefined
				: // DEFAULT: a roster row without a job list holds no job, so a job filter leaves them out.
					new Map(roster.map((nurse) => [nurse.id, (nurse.jobs ?? []).map((job) => job.name)])),
		[roster],
	);
	const balance = useMemo(
		() =>
			people === undefined || jobNamesByNurseId === undefined
				? undefined
				: computeGroupBalance(people, groups, jobName === ALL_JOBS ? null : jobName, jobNamesByNurseId),
		[people, groups, jobName, jobNamesByNurseId],
	);

	const renderTable = () => {
		if (isError) return <HolidaySectionError onRetry={onRetry} dataTestId="holiday-balance-error" />;
		if (balance === undefined) return <JumpingDots size={6} />;
		return (
			<Box sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table size="small" data-testid="holiday-balance-table">
					<TableHead>
						<TableRow>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX}>{t("holidays.balance.column.dayPart")}</TableCell>
							{groups.map((group) => (
								<TableCell key={group.id} sx={CONFIG_TABLE_HEADER_CELL_SX}>
									<HolidayGroupChip groups={groups} groupId={group.id} />
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{BALANCE_ROWS.map((row) => {
							const dayPartLabel = t(row === HOLIDAY_NONE ? NO_DAY_PART_KEY : DAY_PART_LABEL_KEY[row]);
							return (
								<TableRow key={row} data-testid={`holiday-balance-row-${row}`}>
									<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { px: 1.5 }]}>
										<HolidayDayPartLabel dayPart={row === HOLIDAY_NONE ? null : row} />
									</TableCell>
									{groups.map((group) => {
										// DEFAULT: unreachable — `computeGroupBalance` seeds a row for every group it is given.
										const count = balance.counts.get(group.id)?.[row] ?? 0;
										return (
											<TableCell key={group.id} sx={[CONFIG_TABLE_BODY_CELL_SX, { px: 1.5 }]}>
												<HolidayBalanceCell
													count={count}
													rowMax={balance.rowMax[row]}
													color={holidayGroupColor(groups, group.id).dot}
													ariaLabel={t("holidays.balance.cellAria", { count, group: group.name, dayPart: dayPartLabel })}
													onClick={() => {
														const filter = { groupId: group.id, dayPartKey: row, jobName: jobName === ALL_JOBS ? null : jobName };
														track("team_holidays_balance_clicked", { group: group.name, day_part: row, job: filter.jobName, count });
														onShowOnTeam(filter);
													}}
													dataTestId={`holiday-balance-${group.name}-${row}`}
												/>
											</TableCell>
										);
									})}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Box>
		);
	};

	return (
		<SettingsSection
			title={t("holidays.balance.title")}
			icon={<BalanceOutlinedIcon />}
			description={t("holidays.balance.description")}
			layout="stack"
			dataTestId="holiday-balance-section"
			sx={{ boxShadow: SHADOW.PANEL_SOFT }}
		>
			{jobs.length > 1 && (
				<SystemChips<string>
					items={jobItems}
					selectedItems={jobItems.filter((item) => item.value === jobName)}
					onChange={(item) => setJobName(item.value)}
					variant="outlined-brand"
					announcePressed
					forceFitContent
					rootSx={{ justifyContent: "flex-start", flexWrap: "wrap" }}
					dataTestId="holiday-balance-jobs"
					getItemDataTestId={(item) => `holiday-balance-job-${item.value === ALL_JOBS ? "all" : item.value}`}
				/>
			)}
			{renderTable()}
		</SettingsSection>
	);
};
